// @flow

import moment from 'moment-timezone';
import sum from 'lodash/sum';
import type { Challenge } from './Challenge';

// Using a fixed time zone ensures going to vacation does not allow you to cheat :)
//
// We could use UTC, but this would create confusion because
// 1) the day change would not be at midnight in Germany
// 2) the day change would appear at different local times in winter/summer time

export const FixedTimeZoneName = 'Europe/Berlin';

export type CompletionState = 'completed' | 'missed' | 'today' | 'future';

export type Reengagement = {
    challengeId: number,
    campaignId: number,
    callbackTimestamp: string,
    installationTimestamp: string,
    // Array containing `true` for each completed step, `false` for a missed step.
    // Steps in the future are not in the array, so it might be shorter than the number of steps.
    pastStepsFlags: boolean[],
    appPackage: string,
}

export type ReengagementPlan = { [string]: number };


function installationMomentPlusDay(reengagement: Reengagement, day: number) {
    return moment
        .tz(reengagement.installationTimestamp, FixedTimeZoneName)
        .add(day, 'day');
}

export function hasCompletedOffer(reengagement: Reengagement, day: number, dayIndexInPlan: number): ?boolean {
    if (!reengagement) return null;
    const pastStepsFlags = reengagement.pastStepsFlags;
    if (!pastStepsFlags) return null;
    const flag = pastStepsFlags[dayIndexInPlan];
    if (typeof flag === 'undefined') {
        // If the flag is undefined, the day might still be on a past day
        // because the page was open while there was a day change.
        if (installationMomentPlusDay(reengagement, day).endOf('day').toDate() < new Date()) {
            return false;
        }
    }
    return flag;
}

export function stepIsToday(reengagement: Reengagement, day: number): boolean {
    if (!reengagement) return false;
    return moment.tz(FixedTimeZoneName).isSame(installationMomentPlusDay(reengagement, day), 'day');
}

export function humanStepNameFromNow(reengagement: Reengagement, day: number) {
    if (!reengagement) return null;
    return installationMomentPlusDay(reengagement, day).startOf('day').fromNow();
}

export function completionStateFor(reengagement: Reengagement, day:number, dayIndexInPlan: number): CompletionState {
    const isToday = stepIsToday(reengagement, day);
    return {
        true: 'completed',
        false: 'missed',
        undefined: (isToday ? 'today' : 'future')
    }[String(hasCompletedOffer(reengagement, day, dayIndexInPlan))];
}


export function reengagementPlan(challenges: Challenge[]) {
    // Possible edge case: There might be multiple plans defined. Merge them into one.
    const reengagementPlans = challenges
        .filter((challenge) => challenge.sdkType === 're-engagement')
        .map((challenge: Challenge) => challenge.properties && challenge.properties.X_RE_ENGAGEMENT)
        .filter(Boolean);
    return reengagementPlans.reduce((acc, value) => Object.assign(acc, value), {});
}


export function earnedPointsFor(reengagementPlan: ReengagementPlan, reengagement: Reengagement): number {
    return sum(reengagement.pastStepsFlags.map((hasBeenCompleted, index) => {
        const awardedPoints = reengagementPlan[Object.keys(reengagementPlan)[index]] || 0;
        return hasBeenCompleted ? awardedPoints : 0;
    }));
}

export function totalPossiblePointsFor(reengagementPlan: ReengagementPlan): number {
    return sum(Object.keys(reengagementPlan).map(day => reengagementPlan[day]));
}


// WEBPACK FOOTER //
// ./src/lib/Reengagement.js